import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { ILocaleItem } from "./stores/translations-scope-store";
import translationStore from "./stores/translations-store";
import { observer } from "mobx-react";

export interface ITranslationScopeContext {
    get scope(): string;
    get defaultLocale(): string;
    get localeList(): ILocaleItem[];
    get isLoading(): boolean;
    getTranslation(key:string, locale?:string): string,
    getDefaultTranslation(key:string): string,
    getHint(key:string): string | undefined,
    setTranslation(key:string, translation:string, locale?:string, type?:string): void,
    copyTranslations(sourceKey: string, targetKey: string): void;
    save(): Promise<void>;
    isSaving(): boolean;
    hasChanges(keys?: string[]): boolean;
    hasTranslations(keys: string[]): boolean;
    discardChanges(): void;

    setDeleted(key: string): void;
    setDeletedKeys(keys: string[]): void;
    restoreDeleted(key: string): void;
    restoreDeletedKeys(keys: string[]): void;

    get selectedLocale(): string;
    setSelectedLocale(locale:string): void;

    /** used only for missing translation indications */
    setTranslatableKeys(keys: string[]): void;
    hasMissingTranslations(locales?: string[], keys?: string[]): boolean;
}

interface IProps {
    children?: ReactNode;
    scope: string;
    locale?: string;
    defaultLocale?: string;
}

export const TranslationScopeContext = React.createContext<ITranslationScopeContext>({} as ITranslationScopeContext);

const TranslationScope = observer(({ children, scope, locale }: IProps) => {
    const storeRef = useRef(translationStore.loadScope(scope));
    const [selectedLocale, setSelectedLocale] = useState<string>(locale ?? 'en');
    const [translatableKeys, setTranslatableKeys] = useState([] as string[]);

    useEffect(() => {
        if (storeRef.current.scope !== scope) {
            storeRef.current = translationStore.loadScope(scope);
        }

        return () => translationStore.unloadScope(scope);
    }, [scope])

    /** select default merchant's locale in the languages drop down */
    useEffect(() => {
        setSelectedLocale(storeRef.current.defaultLocale);
    }, [storeRef.current.defaultLocale])

    useEffect(() => {
        if (locale) {
            setSelectedLocale(locale);
        }
    }, [locale])

    const store = storeRef.current;
    const contextValue: ITranslationScopeContext = useMemo<ITranslationScopeContext>(() => ({
        get scope() { return store.scope },
        get defaultLocale() { return store.defaultLocale },
        get localeList() { return store.localeList },
        get isLoading() { return store.isLoading },

        getTranslation(key, locale) { return store.getTranslation(key, locale ?? selectedLocale) ?? ''; },
        getDefaultTranslation(key) { return store.getDefaultTranslation(key) ?? ''; },
        setTranslation(key, translation, locale, type) {
            if (!locale) {
                locale = selectedLocale;
            }
            return store.setTranslation(key, locale, translation, type);
        },
        copyTranslations: store.copyTranslations.bind(store),

        save: store.save.bind(store),
        isSaving: store.isSaving.bind(store),
        hasChanges: store.hasChanges.bind(store),
        hasTranslations: store.hasTranslations.bind(store),
        getHint: store.getHint.bind(store),
        discardChanges: store.discardChanges.bind(store),

        setDeleted(key) { return store.setDeleted(key, selectedLocale); },
        setDeletedKeys(keys) { return store.setDeletedKeys(keys); },
        restoreDeleted(key) { return store.restoreDeleted(key, selectedLocale); },
        restoreDeletedKeys(keys) { return store.restoreDeletedKeys(keys); },

        get selectedLocale() { return selectedLocale },
        setSelectedLocale,

        setTranslatableKeys,
        hasMissingTranslations(locales?, keys?) {
            const allLocales = store.localeList.map(locale => locale.value);
            const allKeys = translatableKeys;

            // check certain locale for certain keys
            if (locales && keys) {
                return store.hasMissingTranslations(locales, keys);
            }

            // check certain keys in all locales
            if (keys) {
                return store.hasMissingTranslations(allLocales, keys);
            }

            // check certain locale for all keys
            if (locales) {
                return store.hasMissingTranslations(locales, allKeys);
            }

            // check all locales for all keys
            return store.hasMissingTranslations(allLocales, allKeys);
        },
    }), [store, selectedLocale, translatableKeys]);

    return (
        <TranslationScopeContext.Provider value={contextValue}>
            {children}
        </TranslationScopeContext.Provider>
    );
});

export default TranslationScope;
